import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import Layout, { PageHeader } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { CATEGORY_LABELS } from "../lib/categories";
import { Save, Download, Upload, Search } from "lucide-react";

/**
 * Admin Opening Inventory — bulk-set current_stock per item per location.
 * Manual entry is the default; CSV upload is a shortcut (rows: item_id,current_stock).
 * A sample-CSV download button is provided so the client can fill it out offline.
 */
export default function OpeningInventory() {
  const [locations, setLocations] = useState([]);
  const [locId, setLocId] = useState("");
  const [items, setItems] = useState([]);
  const [stock, setStock] = useState({}); // {item_id: number}
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/locations").then((r) => {
      setLocations(r.data);
      setLocId((prev) => prev || r.data[0]?.id || "");
    });
  }, []);

  useEffect(() => {
    if (!locId) return;
    api.get(`/inventory?location_id=${encodeURIComponent(locId)}`).then((r) => {
      setItems(r.data);
      const seed = {};
      for (const it of r.data) seed[it.id] = it.current_stock ?? 0;
      setStock(seed);
    });
  }, [locId]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return items.filter((i) => !q || i.name.toLowerCase().includes(q));
  }, [items, query]);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { location_id: locId, items: stock };
      const { data } = await api.post("/inventory/opening", payload);
      toast.success(`Opening inventory saved`, { description: `${data.updated_items} items updated.` });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const downloadSample = () => {
    const rows = [["item_id", "name", "current_stock"]];
    for (const it of items) rows.push([it.id, it.name, String(it.current_stock ?? 0)]);
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const locName = (locations.find((l) => l.id === locId)?.name || "location").replace(/\s+/g, "_");
    a.download = `opening_inventory_${locName}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importCsv = (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split(/\r?\n/).filter(Boolean);
        // Skip header row
        const dataLines = lines[0].toLowerCase().includes("item_id") ? lines.slice(1) : lines;
        const parsed = {};
        for (const line of dataLines) {
          const cols = line.split(",").map((c) => c.replace(/^"|"$/g, ""));
          const [id, , qty] = cols;
          if (id && qty !== undefined) parsed[id] = Math.max(0, parseInt(qty, 10) || 0);
        }
        setStock((prev) => ({ ...prev, ...parsed }));
        toast.success(`Loaded ${Object.keys(parsed).length} rows from CSV — click Save to persist.`);
      } catch (err) {
        toast.error("Couldn't parse CSV", { description: err.message });
      }
    };
    reader.readAsText(file);
    ev.target.value = "";
  };

  return (
    <Layout>
      <PageHeader
        eyebrow="Launch"
        title="Opening Inventory"
        description="Set the starting stock for each item at each location. Type numbers manually, or upload a CSV."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={locId}
              onChange={(e) => setLocId(e.target.value)}
              data-testid="opening-location-picker"
              className="h-11 px-3 rounded-xl border border-border bg-card text-sm font-medium"
            >
              {locations.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
            <button
              onClick={downloadSample}
              data-testid="opening-download-csv"
              className="h-11 px-4 rounded-xl border border-border bg-card text-sm font-medium hover:bg-secondary inline-flex items-center gap-2"
            >
              <Download className="h-4 w-4" /> Sample CSV
            </button>
            <label
              data-testid="opening-upload-csv-label"
              className="h-11 px-4 rounded-xl border border-border bg-card text-sm font-medium hover:bg-secondary inline-flex items-center gap-2 cursor-pointer"
            >
              <Upload className="h-4 w-4" /> Upload CSV
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={importCsv}
                data-testid="opening-upload-csv"
                className="hidden"
              />
            </label>
            <button
              onClick={submit}
              disabled={busy}
              data-testid="opening-save-btn"
              className="h-11 px-5 rounded-xl bg-foreground text-background text-sm font-medium hover:bg-foreground/90 inline-flex items-center gap-2 disabled:opacity-50"
            >
              <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save Opening Inventory"}
            </button>
          </div>
        }
      />

      <div className="relative mb-5 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search items…"
          data-testid="opening-search"
          className="h-11 pl-9 pr-3 rounded-xl border border-input bg-card text-sm w-full"
        />
      </div>

      <div className="luxury-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60">
            <tr className="text-left">
              <th className="px-5 py-3 label-eyebrow font-medium">Item</th>
              <th className="px-5 py-3 label-eyebrow font-medium">Category</th>
              <th className="px-5 py-3 label-eyebrow font-medium">Pack</th>
              <th className="px-5 py-3 label-eyebrow font-medium text-right w-40">Opening Stock</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((it) => (
              <tr key={it.id} className="border-t border-border">
                <td className="px-5 py-3 font-medium">{it.name}</td>
                <td className="px-5 py-3 text-muted-foreground">{CATEGORY_LABELS[it.category] || it.category}</td>
                <td className="px-5 py-3 text-muted-foreground">
                  {it.pack_size ? `${it.pack_size} / ${it.unit}` : it.unit}
                </td>
                <td className="px-5 py-3 text-right">
                  <input
                    type="number"
                    min="0"
                    inputMode="numeric"
                    value={stock[it.id] ?? 0}
                    onChange={(e) => setStock((s) => ({ ...s, [it.id]: Math.max(0, parseInt(e.target.value, 10) || 0) }))}
                    data-testid={`opening-stock-${it.id}`}
                    className="h-11 w-28 rounded-lg border border-input bg-card px-3 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan="4" className="px-5 py-12 text-center text-muted-foreground">
                No items match your filter.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
